/**
 * Дашборд site-task: фиксированный период 3 месяца (3 точки) или массив дат из Twig
 * (`site_task_chart_dates` → script[data-site-dash-chart-config]).
 * Прорисовка линии/области/точек графика — через GSAP ScrollTrigger при скролле.
 */

import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const SVG_NS = 'http://www.w3.org/2000/svg'

const DEFAULT_POINTS = 3
const DEFAULT_PERIOD_DAYS = 90

const ORDINAL_MONTH_LABELS = ['первый месяц', 'второй месяц', 'третий месяц']

const MONTHS_RU_GEN = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
]

const CHART_X_LEFT = 96
const CHART_X_RIGHT = 336
/** Края заливки под графиком — как у сетки (twig: 44–392) */
const CHART_FILL_X_LEFT = 44
const CHART_FILL_X_RIGHT = 392
const CHART_Y_TOP = 22
const CHART_Y_BOTTOM = 118
const CHART_AREA_FLOOR = 138
const CHART_VIEW_W = 400
const CHART_VIEW_H = 178
const CHART_X_LABEL_Y = 160

function pad2(n) {
  return String(n).padStart(2, '0')
}

/**
 * @param {Date} d
 * @returns {string}
 */
function formatDateRuLong(d) {
  return `${d.getDate()} ${MONTHS_RU_GEN[d.getMonth()]} ${d.getFullYear()}`
}

/**
 * @param {string} s
 * @returns {Date | null}
 */
function parseFlexibleDate(s) {
  const t = String(s).trim()
  let m = t.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0)
  }
  m = t.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/)
  if (m) {
    return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]), 12, 0, 0)
  }
  const parsed = new Date(t)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/**
 * @typedef {{ date: Date, axis: string | null, leads: number | null }} ChartDateEntry
 * @param {unknown} raw
 * @returns {ChartDateEntry | null}
 */
function parseChartDateEntry(raw) {
  if (typeof raw === 'string') {
    const d = parseFlexibleDate(raw)
    return d ? { date: d, axis: null, leads: null } : null
  }
  if (raw && typeof raw === 'object' && 'date' in raw) {
    const o = /** @type {{ date?: unknown, axis?: unknown, leads?: unknown }} */ (raw)
    const d = parseFlexibleDate(String(o.date ?? ''))
    if (!d) {
      return null
    }
    const axis = o.axis != null && String(o.axis).trim() !== '' ? String(o.axis) : null
    const leadsRaw = o.leads
    const leads =
      leadsRaw != null && leadsRaw !== '' && !Number.isNaN(Number(leadsRaw)) ? Number(leadsRaw) : null
    return { date: d, axis, leads }
  }
  return null
}

/**
 * @param {HTMLElement} root
 * @returns {ChartDateEntry[] | null}
 */
function readChartConfigFromDom(root) {
  const script = root.querySelector('script[data-site-dash-chart-config]')
  if (!script || !script.textContent.trim()) {
    return null
  }
  try {
    const raw = JSON.parse(script.textContent)
    if (!Array.isArray(raw) || raw.length === 0) {
      return null
    }
    const out = []
    for (let i = 0; i < raw.length; i += 1) {
      const e = parseChartDateEntry(raw[i])
      if (e) {
        out.push(e)
      }
    }
    return out.length > 0 ? out : null
  } catch {
    return null
  }
}

/**
 * Последняя точка — сегодня; остальные равномерно по календарю назад в пределах периода.
 * @param {number} n
 * @returns {Date[]}
 */
function getPointDates(n) {
  const today = new Date()
  today.setHours(12, 0, 0, 0)
  const dates = []
  for (let i = 0; i < n; i += 1) {
    const offsetDays = n === 1 ? 0 : Math.round((DEFAULT_PERIOD_DAYS * (n - 1 - i)) / (n - 1))
    const d = new Date(today)
    d.setDate(d.getDate() - offsetDays)
    d.setHours(12, 0, 0, 0)
    dates.push(d)
  }
  return dates
}

/**
 * Подпись оси X: последняя точка — календарный «Сегодня», остальные — дд.мм.
 * @param {Date} d
 * @param {boolean} isLast
 */
function formatAxisLabel(d, isLast) {
  if (isLast) {
    return 'Сегодня'
  }
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}`
}

/**
 * Число заявок по положению точки на шкале (согласовано с кривой).
 * @param {number} y
 */
function yToLeadValue(y) {
  const t = (CHART_Y_BOTTOM - y) / (CHART_Y_BOTTOM - CHART_Y_TOP)
  return Math.max(12, Math.round(24 + t * (196 - 24)))
}

/**
 * @param {number} n
 * @returns {number[]}
 */
function buildTrendY(n) {
  const ys = []
  for (let i = 0; i < n; i += 1) {
    const t = n === 1 ? 1 : i / (n - 1)
    const e = 1 - (1 - t) ** 1.35
    ys.push(CHART_Y_BOTTOM - e * (CHART_Y_BOTTOM - CHART_Y_TOP))
  }
  return ys
}

/**
 * @param {number} n
 * @returns {Array<{ x: number, y: number }>}
 */
function buildPoints(n) {
  const ys = buildTrendY(n)
  const points = []
  for (let i = 0; i < n; i += 1) {
    const x =
      n === 1
        ? (CHART_X_LEFT + CHART_X_RIGHT) / 2
        : CHART_X_LEFT + (i * (CHART_X_RIGHT - CHART_X_LEFT)) / (n - 1)
    points.push({ x, y: ys[i] })
  }
  return points
}

/**
 * @param {Array<{ x: number, y: number }>} pts
 * @returns {string}
 */
function pointsToCurvePath(pts) {
  if (pts.length === 0) {
    return ''
  }
  if (pts.length === 1) {
    return `M ${pts[0].x} ${pts[0].y}`
  }
  let d = `M ${pts[0].x} ${pts[0].y}`
  for (let i = 0; i < pts.length - 1; i += 1) {
    const p0 = i > 0 ? pts[i - 1] : pts[i]
    const p1 = pts[i]
    const p2 = pts[i + 1]
    const p3 = i + 2 < pts.length ? pts[i + 2] : p2
    const cp1x = p1.x + (p2.x - p0.x) / 6
    const cp1y = p1.y + (p2.y - p0.y) / 6
    const cp2x = p2.x - (p3.x - p1.x) / 6
    const cp2y = p2.y - (p3.y - p1.y) / 6
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`
  }
  return d
}

/**
 * Линия от начала до конца области графика (как сетка 44–392): слева на высоте первой точки,
 * вход в первую ключевую точку, сглаженная кривая, выход на правый край на высоте последней.
 * @param {Array<{ x: number, y: number }>} pts
 * @returns {string}
 */
function chartLinePathFullWidth(pts) {
  if (pts.length === 0) {
    return ''
  }
  if (pts.length === 1) {
    const y = pts[0].y
    return `M ${CHART_FILL_X_LEFT} ${y} L ${CHART_FILL_X_RIGHT} ${y}`
  }
  const first = pts[0]
  const last = pts[pts.length - 1]
  const curveTail = pointsToCurvePath(pts).replace(/^M\s+[\d.]+\s+[\d.]+/i, '')
  return `M ${CHART_FILL_X_LEFT} ${first.y} L ${first.x} ${first.y}${curveTail} L ${CHART_FILL_X_RIGHT} ${last.y}`
}

/**
 * Заливка под тем же верхним контуром, что и линия, замкнутая по низу.
 * @param {Array<{ x: number, y: number }>} pts
 * @returns {string}
 */
function chartAreaPathFullWidth(pts) {
  const top = chartLinePathFullWidth(pts)
  if (!top) {
    return ''
  }
  return `${top} L ${CHART_FILL_X_RIGHT} ${CHART_AREA_FLOOR} L ${CHART_FILL_X_LEFT} ${CHART_AREA_FLOOR} Z`
}

/**
 * @param {HTMLElement} root
 * @param {Array<{ x: number, y: number }>} pts
 * @param {Date[]} dates
 * @param {number[]} values
 */
function renderChartHits(root, pts, dates, values) {
  const wrap = root.querySelector('[data-dashboard-chart-hits]')
  if (!(wrap instanceof HTMLElement)) {
    return
  }
  wrap.replaceChildren()
  const rid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`

  for (let i = 0; i < pts.length; i += 1) {
    const tip = document.createElement('div')
    tip.className = 'site-task-dashboard__chart-tip'
    tip.style.left = `${(pts[i].x / CHART_VIEW_W) * 100}%`
    tip.style.top = `${(pts[i].y / CHART_VIEW_H) * 100}%`

    const tid = `site-dash-tt-${rid}-${i}`
    const btn = document.createElement('button')
    btn.type = 'button'
    btn.className = 'site-task-dashboard__chart-tip-hit'
    btn.setAttribute('aria-describedby', tid)

    const dateLong = formatDateRuLong(dates[i])
    const isLast = i === dates.length - 1
    btn.setAttribute(
      'aria-label',
      `${isLast ? 'Сегодня' : dateLong}: показать число заявок и динамику`
    )

    const tt = document.createElement('div')
    tt.className = 'site-task-dashboard__chart-tooltip'
    tt.id = tid
    tt.setAttribute('role', 'tooltip')

    const pDate = document.createElement('p')
    pDate.className = 'site-task-dashboard__chart-tooltip-date'
    pDate.textContent = isLast ? `Сегодня, ${dateLong}` : dateLong

    const pVal = document.createElement('p')
    pVal.className = 'site-task-dashboard__chart-tooltip-text'
    const strong = document.createElement('strong')
    strong.textContent = String(values[i])
    pVal.appendChild(strong)
    pVal.appendChild(document.createTextNode(' заявок'))

    tt.appendChild(pDate)
    tt.appendChild(pVal)

    if (i > 0) {
      const prev = values[i - 1]
      const delta = prev === 0 ? 0 : Math.round(((values[i] - prev) / prev) * 100)
      const pDelta = document.createElement('p')
      pDelta.className = 'site-task-dashboard__chart-tooltip-delta'
      pDelta.textContent =
        delta >= 0 ? `+${delta}% к предыдущей точке` : `${delta}% к предыдущей точке`
      tt.appendChild(pDelta)
    }

    tip.appendChild(btn)
    tip.appendChild(tt)
    wrap.appendChild(tip)
  }
}

/**
 * @param {HTMLElement} root
 * @param {{ alreadyAnimated: boolean }} state
 */
function renderMainChart(root, state) {
  /** @type {ChartDateEntry[] | null} */
  const customEntries = readChartConfigFromDom(root)

  let n
  /** @type {Date[]} */
  let dates
  /** @type {ChartDateEntry[]} */
  let entries

  if (customEntries) {
    n = customEntries.length
    entries = customEntries
    dates = customEntries.map((e) => e.date)
  } else {
    n = DEFAULT_POINTS
    dates = getPointDates(n)
    entries = dates.map((d) => ({ date: d, axis: null, leads: null }))
  }

  const pts = buildPoints(n)
  const values = entries.map((e, i) =>
    e.leads != null ? e.leads : yToLeadValue(pts[i].y)
  )
  const lineD = chartLinePathFullWidth(pts)
  const areaD = chartAreaPathFullWidth(pts)

  const labelsG = root.querySelector('[data-dashboard-chart-labels]')
  const area = root.querySelector('[data-dashboard-chart-area]')
  const line = root.querySelector('[data-dashboard-chart-line]')
  const dotsG = root.querySelector('[data-dashboard-chart-dots]')

  if (!(labelsG instanceof SVGGElement) || !(area instanceof SVGPathElement) || !(line instanceof SVGPathElement) || !(dotsG instanceof SVGGElement)) {
    return
  }

  labelsG.replaceChildren()
  labelsG.classList.remove('site-task-dashboard__chart-labels--dense', 'site-task-dashboard__chart-labels--xdense')
  if (n > 9) {
    labelsG.classList.add('site-task-dashboard__chart-labels--xdense')
  } else if (n > 6) {
    labelsG.classList.add('site-task-dashboard__chart-labels--dense')
  }

  for (let i = 0; i < n; i += 1) {
    const text = document.createElementNS(SVG_NS, 'text')
    text.setAttribute('x', String(pts[i].x))
    text.setAttribute('y', String(CHART_X_LABEL_Y))
    text.setAttribute('text-anchor', 'middle')
    text.setAttribute('dominant-baseline', 'middle')
    const axisFromData = entries[i].axis
    let labelText
    if (axisFromData) {
      labelText = axisFromData
    } else if (n === 3) {
      labelText = ORDINAL_MONTH_LABELS[i]
    } else {
      labelText = formatAxisLabel(dates[i], i === n - 1)
    }
    text.textContent = labelText
    labelsG.appendChild(text)
  }

  line.setAttribute('d', lineD)
  area.setAttribute('d', areaD)

  dotsG.replaceChildren()
  for (let i = 0; i < n; i += 1) {
    const c = document.createElementNS(SVG_NS, 'circle')
    c.setAttribute('cx', String(pts[i].x))
    c.setAttribute('cy', String(pts[i].y))
    c.setAttribute('r', i === n - 1 ? '4.75' : '3.25')
    c.classList.add('site-task-dashboard__chart-dot')
    c.style.setProperty('--dot-i', String(i))
    if (i === n - 1) {
      c.classList.add('site-task-dashboard__chart-dot--pulse')
    }
    dotsG.appendChild(c)
  }

  renderChartHits(root, pts, dates, values)

  const len = Math.ceil(line.getTotalLength())
  line.style.setProperty('--dash-len', `${len}`)
  line.style.strokeDasharray = String(len)
  line.style.strokeDashoffset = String(len)

  if (state.alreadyAnimated || root.classList.contains('site-task-dashboard--reduced')) {
    line.style.strokeDashoffset = '0'
    area.style.opacity = '1'
    area.style.transform = 'none'
    dotsG.querySelectorAll('.site-task-dashboard__chart-dot').forEach((d) => {
      if (d instanceof SVGElement) {
        d.style.opacity = '1'
      }
    })
  }
}

/**
 * Скролл-привязанная прорисовка графика: линия (stroke-dashoffset),
 * заливка (opacity) и точки (opacity со stagger). Прогресс таймлайна
 * скрабится скроллом по chart-wrap.
 *
 * @param {HTMLElement} root
 */
function setupChartScrollAnimation(root) {
  const chartWrap = root.querySelector('.site-task-dashboard__chart-wrap')
  const svg = root.querySelector('.site-task-dashboard__chart')
  const line = root.querySelector('[data-dashboard-chart-line]')
  const area = root.querySelector('[data-dashboard-chart-area]')
  const dots = root.querySelectorAll('.site-task-dashboard__chart-dot')

  if (
    !(chartWrap instanceof HTMLElement) ||
    !(svg instanceof SVGSVGElement) ||
    !(line instanceof SVGPathElement) ||
    !(area instanceof SVGPathElement)
  ) {
    return
  }

  const dashLen = parseFloat(line.style.getPropertyValue('--dash-len')) || Math.ceil(line.getTotalLength()) || 420

  gsap.set(line, { strokeDasharray: dashLen, strokeDashoffset: dashLen })
  gsap.set(area, { opacity: 1 })
  gsap.set(dots, { opacity: 0, scale: 0.8, transformOrigin: '50% 50%' })

  // Синхронное раскрытие заливки под линией: clipPath-rect скрабится вместе с линией.
  const rid = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`
  const clipId = `site-dash-area-clip-${rid}`
  const fullW = CHART_FILL_X_RIGHT - CHART_FILL_X_LEFT

  let defs = svg.querySelector('defs')
  if (!defs) {
    defs = document.createElementNS(SVG_NS, 'defs')
    svg.insertBefore(defs, svg.firstChild)
  }

  const clip = document.createElementNS(SVG_NS, 'clipPath')
  clip.setAttribute('id', clipId)
  clip.setAttribute('clipPathUnits', 'userSpaceOnUse')
  const rect = document.createElementNS(SVG_NS, 'rect')
  rect.setAttribute('x', String(CHART_FILL_X_LEFT))
  rect.setAttribute('y', '0')
  rect.setAttribute('width', '0')
  rect.setAttribute('height', String(CHART_VIEW_H))
  clip.appendChild(rect)
  defs.appendChild(clip)
  area.setAttribute('clip-path', `url(#${clipId})`)

  // Двигаем край заливки за концом линии (по длине пути), а не по X-ширине.
  const driver = { p: 0 }

  const tl = gsap.timeline({
    defaults: { ease: 'none' },
    scrollTrigger: {
      trigger: chartWrap,
      // Чтобы успевать считать график: стартуем раньше и завершаем примерно к середине блока
      // (на быстрых скроллах не "догоняет" в самом конце секции).
      start: 'top 92%',
      end: 'top 55%',
      scrub: 0.25,
    },
  })

  tl.to(driver, {
    p: 1,
    duration: 1,
    onUpdate: () => {
      const drawn = Math.max(0, Math.min(dashLen, dashLen * driver.p))
      const pt = line.getPointAtLength(drawn)
      const w = Math.max(0, Math.min(fullW, pt.x - CHART_FILL_X_LEFT))
      line.style.strokeDashoffset = String(dashLen - drawn)
      rect.setAttribute('width', String(w))
    },
  }, 0)
    .to(
      dots,
      {
        opacity: 1,
        scale: 1,
        duration: 0.4,
        stagger: 0.12,
      },
      0.55
    )
}

/**
 * GSAP reveal для “Воронки роста”: появление вниз, скрытие вверх.
 * Без pin/фиксации — только легкий stagger.
 *
 * @param {HTMLElement} root
 */
function setupFlowReveal(root) {
  const card = root.querySelector('.site-task-dashboard__card--funnel')
  const items = root.querySelectorAll('.growth-funnel .funnel-step')
  const back = root.querySelector('.growth-funnel__back')

  if (!(card instanceof HTMLElement) || items.length === 0) {
    return
  }

  if (back instanceof HTMLElement) {
    gsap.set(back, { scaleX: 0.08, opacity: 0.2, transformOrigin: 'left center' })
  }
  gsap.set(items, { opacity: 0, y: 10, filter: 'blur(3px)' })

  const tl = gsap.timeline({ paused: true, defaults: { ease: 'power2.out' } })
  if (back instanceof HTMLElement) {
    tl.to(back, { scaleX: 1, opacity: 1, duration: 0.5 }, 0)
  }
  tl.to(items, { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.35, stagger: 0.06 }, 0.06)

  ScrollTrigger.create({
    trigger: card,
    start: 'top 68%',
    end: 'bottom 35%',
    onEnter: () => tl.play(),
    onEnterBack: () => tl.play(),
    onLeaveBack: () => tl.progress(0).pause(),
  })
}

export function initSiteTaskDashboard() {
  const root = document.querySelector('[data-site-task-dashboard]')
  if (!root) {
    return
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (reducedMotion) {
    root.classList.add('site-task-dashboard--reduced')
  }

  const state = { alreadyAnimated: false }

  renderMainChart(root, state)

  root.querySelectorAll('[data-dashboard-spark]').forEach((el) => {
    if (el instanceof SVGGeometryElement) {
      const len = Math.ceil(el.getTotalLength())
      el.style.setProperty('--spark-len', `${len}`)
    }
  })

  root.dataset.chartPeriod = root.hasAttribute('data-site-dash-custom-dates') ? 'custom' : '3m'

  if (reducedMotion) {
    return
  }

  setupChartScrollAnimation(root)
  setupFlowReveal(root)

  // Класс оставляем для остальных анимаций (если есть), но без эффекта “стоп-скролла”.
  ScrollTrigger.create({
    trigger: root,
    start: 'top 80%',
    onEnter: () => {
      root.classList.add('site-task-dashboard--animate')
      state.alreadyAnimated = true
    },
  })
}
